import { useEffect, useMemo, useState } from 'react'
import { AlertTriangle, ChevronDown, ChevronRight, Trash2 } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'

const PLACEHOLDER_RE = /\{\{(\d+)\}\}/g

export interface TemplateVariablesEditorProps {
  body: string
  variables: string[]
  onChange: (variables: string[]) => void
  onValidityChange?: (valid: boolean, errors: string[]) => void
  disabled?: boolean
}

interface VarRow {
  index: number
  name: string
  inBody: boolean
}

function extractPlaceholders(body: string): number[] {
  const set = new Set<number>()
  for (const match of body.matchAll(PLACEHOLDER_RE)) {
    const raw = match[1]
    if (raw === undefined) continue
    const n = Number.parseInt(raw, 10)
    if (Number.isFinite(n) && n > 0) set.add(n)
  }
  return [...set].sort((a, b) => a - b)
}

function hasZeroPlaceholder(body: string): boolean {
  for (const match of body.matchAll(PLACEHOLDER_RE)) {
    if (match[1] !== undefined && Number.parseInt(match[1], 10) === 0) return true
  }
  return false
}

function validateSequence(placeholders: number[]): string[] {
  if (placeholders.length === 0) return []
  const errors: string[] = []
  const first = placeholders[0] ?? 0
  if (first !== 1) {
    errors.push(`Placeholders devem começar em {{1}} (começam em {{${first}}}).`)
  }
  for (let i = 1; i < placeholders.length; i++) {
    const curr = placeholders[i] ?? 0
    const prev = placeholders[i - 1] ?? 0
    if (curr !== prev + 1) {
      errors.push(
        `Sequência quebrada: {{${prev}}} → {{${curr}}}. Use placeholders contíguos.`,
      )
    }
  }
  return errors
}

export function TemplateVariablesEditor({
  body,
  variables,
  onChange,
  onValidityChange,
  disabled = false,
}: TemplateVariablesEditorProps): React.ReactElement {
  const placeholders = useMemo(() => extractPlaceholders(body), [body])
  const hasZero = useMemo(() => hasZeroPlaceholder(body), [body])
  const sequenceErrors = useMemo(() => validateSequence(placeholders), [placeholders])
  const maxPlaceholder =
    placeholders.length === 0 ? 0 : (placeholders[placeholders.length - 1] ?? 0)

  const rows: VarRow[] = useMemo(() => {
    const total = Math.max(maxPlaceholder, variables.length)
    const out: VarRow[] = []
    for (let i = 1; i <= total; i++) {
      out.push({
        index: i,
        name: variables[i - 1] ?? '',
        inBody: placeholders.includes(i),
      })
    }
    return out
  }, [maxPlaceholder, variables, placeholders])

  const missingNames = rows.filter((r) => r.inBody && r.name.trim() === '').length
  const orphanCount = rows.filter((r) => !r.inBody).length
  const allErrors = useMemo(() => {
    const errs = [...sequenceErrors]
    if (hasZero) {
      errs.push('{{0}} não é permitido — placeholders começam em {{1}}.')
    }
    if (missingNames > 0) {
      errs.push(`${missingNames} variável(is) usadas no body sem nome.`)
    }
    if (orphanCount > 0) {
      errs.push(`${orphanCount} variável(is) cadastradas mas não usadas no body.`)
    }
    return errs
  }, [sequenceErrors, hasZero, missingNames, orphanCount])

  const valid = allErrors.length === 0

  useEffect(() => {
    onValidityChange?.(valid, allErrors)
  }, [valid, allErrors, onValidityChange])

  function setVarAt(index: number, value: string): void {
    const next = [...variables]
    while (next.length < index) next.push('')
    next[index - 1] = value
    onChange(next)
  }

  function removeAt(index: number): void {
    const next = variables.filter((_, i) => i !== index - 1)
    onChange(next)
  }

  // JSON escape hatch (mesmo padrão do BusinessHoursEditor)
  const [jsonExpanded, setJsonExpanded] = useState<boolean>(false)
  const [jsonDraft, setJsonDraft] = useState<string>('')
  const [jsonError, setJsonError] = useState<string | null>(null)

  useEffect(() => {
    if (!jsonExpanded) {
      setJsonDraft(JSON.stringify(variables, null, 2))
      setJsonError(null)
    }
  }, [variables, jsonExpanded])

  function applyJson(): void {
    try {
      const parsed = JSON.parse(jsonDraft) as unknown
      if (!Array.isArray(parsed) || !parsed.every((v) => typeof v === 'string')) {
        throw new Error('Esperado um array de strings, ex.: ["nome", "data"]')
      }
      onChange(parsed)
      setJsonError(null)
      setJsonExpanded(false)
    } catch (e) {
      setJsonError((e as Error).message)
    }
  }

  return (
    <div className="grid gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Label className="text-sm font-medium">Variáveis</Label>
          <Badge variant={valid ? 'secondary' : 'warning'}>
            {rows.length} {rows.length === 1 ? 'variável' : 'variáveis'}
          </Badge>
        </div>
        <p className="text-xs text-muted-foreground">
          Use {'{{'}1{'}}'}, {'{{'}2{'}}'}… no corpo. Dê um nome semântico a cada uma.
        </p>
      </div>

      {rows.length === 0 && (
        <div className="rounded-md border border-dashed p-4 text-center text-sm text-muted-foreground">
          Nenhuma variável detectada. Adicione {'{{'}1{'}}'} no corpo para criar a primeira.
        </div>
      )}

      {rows.length > 0 && (
        <div className="grid gap-2">
          {rows.map((row) => (
            <div
              key={row.index}
              className={cn(
                'flex items-center gap-2 rounded-md border p-2',
                !row.inBody && 'border-amber-500/40 bg-amber-500/5',
              )}
            >
              <code className="min-w-[3.5rem] rounded bg-muted px-2 py-1 text-center text-xs font-mono">
                {'{{'}
                {row.index}
                {'}}'}
              </code>
              <Input
                value={row.name}
                onChange={(e) => setVarAt(row.index, e.target.value)}
                placeholder={row.inBody ? 'ex.: nome_lead' : 'órfã — sem placeholder'}
                disabled={disabled}
                className="flex-1"
                aria-label={`Nome da variável ${row.index}`}
              />
              {!row.inBody && (
                <Badge variant="warning" className="whitespace-nowrap">
                  não usada
                </Badge>
              )}
              {!row.inBody && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => removeAt(row.index)}
                  disabled={disabled}
                  aria-label={`Remover variável ${row.index}`}
                >
                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      {allErrors.length > 0 && (
        <div className="rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-xs">
          <div className="mb-1 flex items-center gap-1 font-medium text-amber-700 dark:text-amber-400">
            <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
            Atenção
          </div>
          <ul className="list-disc pl-5">
            {allErrors.map((err, i) => (
              <li key={i}>{err}</li>
            ))}
          </ul>
        </div>
      )}

      <div>
        <button
          type="button"
          onClick={() => setJsonExpanded((v) => !v)}
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          disabled={disabled}
        >
          {jsonExpanded ? (
            <ChevronDown className="h-3 w-3" aria-hidden="true" />
          ) : (
            <ChevronRight className="h-3 w-3" aria-hidden="true" />
          )}
          Editar como JSON (avançado)
        </button>
        {jsonExpanded && (
          <div className="mt-2 grid gap-2">
            <Textarea
              value={jsonDraft}
              onChange={(e) => setJsonDraft(e.target.value)}
              rows={4}
              className="font-mono text-xs"
              disabled={disabled}
              aria-label="Variáveis como JSON"
            />
            {jsonError !== null && (
              <p className="text-xs text-destructive">{jsonError}</p>
            )}
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setJsonExpanded(false)}
                disabled={disabled}
              >
                Cancelar
              </Button>
              <Button type="button" size="sm" onClick={applyJson} disabled={disabled}>
                Aplicar JSON
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default TemplateVariablesEditor
